import React, { useMemo, useState } from 'react';
import { Dialog, OptionRow, TextButton } from '../ui';
import { CreditCard, PayoffPlan } from '../../types';
import { solveForDate, solveForPayment } from '../../utils/finance/planMath';
import { today as todayFn } from '../../utils/finance/dates';
import { toPlanPayload, usd } from './planPayload';

export type ReconcileChoice = 'KEEP_PAYMENT' | 'KEEP_DATE' | 'LEAVE_AS_IS';

interface BalanceReconcileDialogProps {
  card: CreditCard;
  plan: PayoffPlan;
  oldBalance: number;
  newBalance: number;
  /** Returns the plan payload to PUT, or null for "leave as-is". */
  onResolve: (choice: ReconcileChoice, payload: any | null) => Promise<void>;
  onCancel: () => void;
}

/**
 * Requirement 6: a manual balance edit on a card with an active plan is
 * ambiguous — did the user mean "same payment, finish sooner" or "same
 * deadline, pay less"? Ask rather than guess.
 *
 * Each option previews its outcome before committing, because "keep the payoff
 * date" can require an unaffordable payment when the balance jumps.
 */
export const BalanceReconcileDialog: React.FC<BalanceReconcileDialogProps> = ({
  card,
  plan,
  oldBalance,
  newBalance,
  onResolve,
  onCancel,
}) => {
  const [busy, setBusy] = useState<ReconcileChoice | null>(null);
  const originAsOf = useMemo(() => todayFn(), []);

  const scheduleInput = useMemo(
    () => ({
      startingBalance: newBalance,
      apr: card.interestRate,
      dueDay: card.dueDay ?? 1,
      minFloor: card.minimumPayment,
      principalPct: card.minPaymentPrincipalPercent ?? 1,
      originAsOf,
    }),
    [newBalance, card, originAsOf]
  );

  const currentMonthly =
    plan.payments.find(p => p.kind === 'SCHEDULED')?.amount ??
    plan.targetPayment ??
    0;

  const keepPayment = useMemo(
    () => solveForDate(scheduleInput, currentMonthly),
    [scheduleInput, currentMonthly]
  );

  const keepDate = useMemo(
    () => solveForPayment(scheduleInput, plan.endDate),
    [scheduleInput, plan.endDate]
  );

  const wentDown = newBalance < oldBalance;

  const commit = async (choice: ReconcileChoice) => {
    setBusy(choice);
    try {
      if (choice === 'LEAVE_AS_IS') {
        await onResolve(choice, null);
        return;
      }
      const solvedResult = choice === 'KEEP_PAYMENT' ? keepPayment.result : keepDate.result;
      await onResolve(
        choice,
        toPlanPayload({
          card,
          mode: choice === 'KEEP_PAYMENT' ? 'TARGET_PAYMENT' : 'TARGET_DATE',
          result: solvedResult,
          name: plan.name,
          targetDate: choice === 'KEEP_DATE' ? plan.endDate : null,
          targetPayment: choice === 'KEEP_PAYMENT' ? currentMonthly : null,
          originBalance: newBalance,
          originAsOf,
        })
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      wide
      title={`${card.name} balance changed`}
      subhead={
        <>
          {usd(oldBalance)} → <span className="font-semibold text-ink">{usd(newBalance)}</span>{' '}
          ({wentDown ? 'down' : 'up'} {usd(Math.abs(newBalance - oldBalance))}). This card has an
          active payoff plan. What should happen to it?
        </>
      }
      footer={
        <TextButton onClick={onCancel} disabled={!!busy}>
          Cancel — don't save the balance change
        </TextButton>
      }
    >
      <div className="flex flex-col gap-3">
        <OptionRow
          title="Keep the same monthly payment"
          detail={
            keepPayment.result.payoffDate
              ? `Stay at ${usd(currentMonthly)}/mo — now paid off ${keepPayment.result.payoffDate.toLocaleDateString(
                  'en-US',
                  { month: 'long', year: 'numeric' }
                )} (${usd(keepPayment.result.totalInterest)} interest).`
              : `Stay at ${usd(currentMonthly)}/mo — but this no longer clears the balance.`
          }
          warning={keepPayment.warning}
          busy={busy === 'KEEP_PAYMENT'}
          disabled={!!busy}
          onClick={() => commit('KEEP_PAYMENT')}
        />

        <OptionRow
          title="Keep the same payoff date"
          detail={`Adjust to ${usd(keepDate.monthlyPayment)}/mo to still finish ${plan.endDate.toLocaleDateString(
            'en-US',
            { month: 'long', year: 'numeric' }
          )} (${usd(keepDate.result.totalInterest)} interest).`}
          warning={keepDate.warning}
          busy={busy === 'KEEP_DATE'}
          disabled={!!busy}
          onClick={() => commit('KEEP_DATE')}
        />

        <OptionRow
          title="Leave the plan alone"
          detail="Keep the existing schedule exactly as it is. The plan is marked out of date, and projections use its payment amounts against your real balance."
          busy={busy === 'LEAVE_AS_IS'}
          disabled={!!busy}
          onClick={() => commit('LEAVE_AS_IS')}
        />
      </div>
    </Dialog>
  );
};
